import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from "../../../Shared/Dialog";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";
import Button from "../../../Shared/Buttons/Button";
import Chip from "../../../Shared/ui/Chip";
import { fmt } from "../../../../Utils/formatCurrency";

// Dialog to configure a single substitution: pick the replacement price definition and choose
// which occurrences it applies to (all, or specific bundles and/or direct price definitions),
// all in one place. Confirms with { substituteItemId, scope } (scope === null means all occurrences),
// or null to remove the substitution.
export default function SubstitutionDialog({ open, def, value, substituteOptions, currency, onClose, onConfirm }) {
  const { t } = useTranslation();

  const [substituteItemId, setSubstituteItemId] = useState(null);
  // scope === null means "all occurrences"; otherwise { bundleIds:[], direct:bool }.
  const [scope, setScope] = useState(null);

  useEffect(() => {
    if (open) {
      setSubstituteItemId(value?.substituteItemId ?? null);
      setScope(value?.scope ?? null);
    }
  }, [open, value]);

  if (!def) return null;

  const bundles = def.bundles || [];
  const hasDirect = def.hasDirect;
  const isAll = !scope;
  const selectedBundleIds = scope?.bundleIds || [];
  const directOn = !!scope?.direct;

  const substitute = substituteItemId != null ? substituteOptions.find((o) => o.id === substituteItemId) : null;
  const subPrice = substitute?.currentValue ?? null;
  const unpriced = substitute != null && subPrice == null;
  const uomMismatch = substitute && substitute.uom && def.uom && substitute.uom !== def.uom;
  const delta = substitute && subPrice != null ? subPrice - (def.currentValue ?? 0) : null;
  const isRevenue = def.group === "Revenue";
  const deltaVariant = delta == null || Math.abs(delta) < 0.001
    ? "default"
    : isRevenue ? (delta > 0 ? "success" : "danger") : (delta > 0 ? "danger" : "success");

  // Explicit-mode helpers: leaving "All" seeds with every occurrence selected.
  const explicitScope = () => (scope ? { bundleIds: [...selectedBundleIds], direct: directOn } : { bundleIds: bundles.map((b) => b.id), direct: hasDirect });
  const setExplicit = (next) => {
    const coversAll = next.bundleIds.length === bundles.length && (!hasDirect || next.direct);
    setScope(coversAll ? null : next);
  };
  const toggleAll = () => setScope(null);
  const toggleBundle = (id) => {
    const s = explicitScope();
    s.bundleIds = s.bundleIds.includes(id) ? s.bundleIds.filter((x) => x !== id) : [...s.bundleIds, id];
    setExplicit(s);
  };
  const toggleDirect = () => {
    const s = explicitScope();
    s.direct = !s.direct;
    setExplicit(s);
  };

  const nothingInScope = !isAll && selectedBundleIds.length === 0 && !directOn;
  const canConfirm = substituteItemId != null && !nothingInScope;

  const options = substituteOptions.filter((o) => o.id !== def.id);

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(def.id, { substituteItemId, scope });
  };

  const rowStyle = { display: "flex", alignItems: "center", gap: 10, padding: "7px 4px", fontSize: 13, cursor: "pointer" };
  const sectionLabel = { fontSize: 11, textTransform: "uppercase", letterSpacing: "0.6px", color: "var(--color-text-secondary)", fontFamily: "'DM Mono', monospace", marginBottom: 6 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <DialogHeader>
        <DialogTitle>{t('projectionsTab.substituteDialogTitle')}</DialogTitle>
        <DialogDescription>
          {def.code ? `${def.code} - ${def.name}` : def.name} · {fmt(def.currentValue ?? 0, currency, 2)}{def.uom ? ` / ${def.uom}` : ""}
        </DialogDescription>
      </DialogHeader>
      <DialogBody>
        {/* Substitute picker */}
        <div style={{ marginBottom: 18 }}>
          <div style={sectionLabel}>{t('projectionsTab.substituteWith')}</div>
          <Select
            value={substituteItemId}
            onChange={(opt) => setSubstituteItemId(opt ? opt.id : null)}
            searchable
            fullwidth
            size="medium"
            placeholder={t('projectionsTab.selectSubstitute')}
          >
            <SelectTrigger>{substitute ? (substitute.code ? `${substitute.code} - ${substitute.name}` : substitute.name) : ""}</SelectTrigger>
            <SelectContent>
              {options.map((o) => (
                <SelectItem key={o.id} value={o} searchText={`${o.code ?? ""} ${o.name}`}>
                  <span style={{ display: "flex", justifyContent: "space-between", gap: 10, width: "100%" }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {o.code ? `${o.code} - ${o.name}` : o.name}
                    </span>
                    <span style={{ fontFamily: "'DM Mono', monospace", opacity: 0.7, whiteSpace: "nowrap" }}>
                      {o.currentValue != null ? fmt(o.currentValue, currency, 2) : t('projectionsTab.noPrice')}{o.uom ? `/${o.uom}` : ""}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(unpriced || uomMismatch || delta != null) && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {delta != null && Math.abs(delta) >= 0.001 && (
                <Chip variant={deltaVariant} size="small">
                  {delta > 0 ? "+" : ""}{fmt(delta, currency, 2)}
                </Chip>
              )}
              {unpriced && <Chip variant="warning" size="small">{t('projectionsTab.noCurrentPrice')}</Chip>}
              {uomMismatch && <Chip variant="warning" size="small">{t('projectionsTab.uomMismatch', { from: def.uom, to: substitute.uom })}</Chip>}
            </div>
          )}
        </div>

        {/* Scope */}
        <div>
          <div style={sectionLabel}>{t('projectionsTab.applyTo')}</div>
          <label style={rowStyle}>
            <input type="checkbox" checked={isAll} onChange={toggleAll} />
            <span>{t('projectionsTab.allOccurrences')}</span>
          </label>
          {(bundles.length > 0 || hasDirect) && <div style={{ height: 1, background: "var(--color-border-light)", margin: "4px 0" }} />}
          {bundles.map((b) => (
            <label key={b.id} style={rowStyle}>
              <input type="checkbox" checked={isAll || selectedBundleIds.includes(b.id)} onChange={() => toggleBundle(b.id)} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</span>
            </label>
          ))}
          {hasDirect && (
            <label style={rowStyle}>
              <input type="checkbox" checked={isAll || directOn} onChange={toggleDirect} />
              <span>{t('projectionsTab.directPriceDefinitions')}</span>
            </label>
          )}
          {nothingInScope && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--color-text-danger)" }}>{t('projectionsTab.selectAtLeastOneScope')}</div>
          )}
        </div>
      </DialogBody>
      <DialogFooter style={{ justifyContent: "space-between" }}>
        <div>
          {value?.substituteItemId != null && (
            <Button variant="destructive" onClick={() => onConfirm(def.id, null)}>{t('projectionsTab.removeSubstitution')}</Button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!canConfirm} onClick={handleConfirm}>{t('common.apply')}</Button>
        </div>
      </DialogFooter>
    </Dialog>
  );
}
